import React, { useEffect, useState, useCallback } from "react";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import { Collapse } from "reactstrap";
import { withTranslation } from "react-i18next";
import withRouter from "../../Components/Common/withRouter";
import { useSelector } from "react-redux";
import { createSelector } from "reselect";
import { useNavData } from "Components/Hooks/useNavData";

const VerticalLayout: React.FC<any> = (props) => {
  // State variables for nav hook (matching those used in HorizontalLayout)
  const [menuState, setMenuState] = useState<Record<string, boolean>>({
    dashboard: false,
    students: false,
    applications: false,
    institutes: false,
    courses: false,
    finances: false,
    sources: false,
    subagents: false,
    channels: false,
    fields: false,
    users: false,
    roles: false,
    teams: false,
    companies: false,
    forms: false,
    products: false,
    templates: false,
    reports: false,
    imports: false,
  });
  const [currentState, setCurrentState] = useState("Dashboard");

  // Helper: update sidebar icons when a dropdown is toggled.
  const updateIconSidebar = useCallback((e: any) => {
    if (e && e.target && e.target.getAttribute("sub-items")) {
      const ul = document.getElementById("two-column-menu");
      if (ul) {
        const iconItems = ul.querySelectorAll(".nav-icon.active");
        Array.from(iconItems).forEach((item) => {
          const element = item as HTMLElement;
          element.classList.remove("active");
          const id = element.getAttribute("sub-items");
          const targetEl = document.getElementById(id || "");
          if (targetEl) {
            targetEl.classList.remove("show");
          }
        });
      }
    }
  }, []);

  // Retrieve navigation data via the custom hook
  const navData = useNavData(menuState, setMenuState, setCurrentState, updateIconSidebar);
  const path = props.router.location.pathname;

  // Redux selector for layout properties
  const selectLayoutProperties = createSelector(
    (state: any) => state.Layout,
    (layout) => ({
      leftsidbarSizeType: layout.leftsidbarSizeType,
      sidebarVisibilitytype: layout.sidebarVisibilitytype,
      layoutType: layout.layoutType,
    })
  );
  const { leftsidbarSizeType, sidebarVisibilitytype, layoutType } =
    useSelector(selectLayoutProperties);

  // Resize event handler to adjust sidebar behavior on window resize.
  const resizeSidebarMenu = useCallback(() => {
    const windowSize = document.documentElement.clientWidth;
    const humberIcon = document.querySelector(".hamburger-icon") as HTMLElement | null;
    if (windowSize >= 1025) {
      const currentLayout = document.documentElement.getAttribute("data-layout");
      if (currentLayout === "vertical" || currentLayout === "semibox") {
        if (leftsidbarSizeType) {
          document.documentElement.setAttribute("data-sidebar-size", leftsidbarSizeType);
        }
      }
      const hamburgerIcon = document.querySelector(".hamburger-icon");
      if (hamburgerIcon !== null) {
        if (
          sidebarVisibilitytype === "show" ||
          layoutType === "vertical" ||
          layoutType === "twocolumn"
        ) {
          hamburgerIcon.classList.remove("open");
        } else {
          hamburgerIcon.classList.add("open");
        }
      }
    } else if (windowSize < 1025 && windowSize > 767) {
      document.body.classList.remove("twocolumn-panel");
      const currentLayout = document.documentElement.getAttribute("data-layout");
      if (currentLayout === "vertical" || currentLayout === "semibox") {
        document.documentElement.setAttribute("data-sidebar-size", "sm");
      }
      if (humberIcon) {
        humberIcon.classList.add("open");
      }
    } else if (windowSize <= 767) {
      document.body.classList.remove("vertical-sidebar-enable");
      if (document.documentElement.getAttribute("data-layout") !== "horizontal") {
        document.documentElement.setAttribute("data-sidebar-size", "lg");
      }
      if (humberIcon) {
        humberIcon.classList.add("open");
      }
    }
  }, [leftsidbarSizeType, sidebarVisibilitytype, layoutType]);

  useEffect(() => {
    window.addEventListener("resize", resizeSidebarMenu, true);
    return () => {
      window.removeEventListener("resize", resizeSidebarMenu, true);
    };
  }, [resizeSidebarMenu]);

  // On route change, scroll to top and initialize menu activation.
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    const initMenu = () => {
      const pathName = (process.env.PUBLIC_URL || "") + path;
      const ul = document.getElementById("navbar-nav");
      if (!ul) return;
      const items = ul.getElementsByTagName("a");
      const itemsArray = Array.from(items) as HTMLAnchorElement[];
      removeActivation(itemsArray);
      const matchingMenuItem = itemsArray.find((x) => x.pathname === pathName);
      if (matchingMenuItem) {
        activateParentDropdown(matchingMenuItem);
      }
    };
    initMenu();
  }, [path, props.layoutType]);

  // Activate parent dropdown for nested menu items.
  function activateParentDropdown(item: HTMLElement): boolean {
    item.classList.add("active");
    const parentCollapseDiv = item.closest(".collapse.menu-dropdown") as HTMLElement | null;
    if (parentCollapseDiv) {
      parentCollapseDiv.classList.add("show");
      if (parentCollapseDiv.parentElement?.children[0]) {
        (parentCollapseDiv.parentElement.children[0] as HTMLElement).classList.add("active");
        parentCollapseDiv.parentElement.children[0].setAttribute("aria-expanded", "true");
      }
      const grandParentCollapse = parentCollapseDiv.parentElement?.closest(".collapse.menu-dropdown") as HTMLElement | null;
      if (grandParentCollapse) {
        grandParentCollapse.classList.add("show");
        if (grandParentCollapse.previousElementSibling) {
          (grandParentCollapse.previousElementSibling as HTMLElement).classList.add("active");
        }
        const greatGrandParentCollapse = grandParentCollapse.previousElementSibling?.closest(".collapse") as HTMLElement | null;
        if (greatGrandParentCollapse && greatGrandParentCollapse.previousElementSibling) {
          greatGrandParentCollapse.classList.add("show");
          (greatGrandParentCollapse.previousElementSibling as HTMLElement).classList.add("active");
        }
      }
      return false;
    }
    return false;
  }

  // Remove activation classes from all menu items.
  const removeActivation = (items: HTMLAnchorElement[]) => {
    items.forEach((item) => {
      item.classList.remove("active");
      item.setAttribute("aria-expanded", "false");
      if (item.nextElementSibling) item.nextElementSibling.classList.remove("show");
    });
  };

  return (
    <>
      {navData.map((item: any, key: number) => (
        <React.Fragment key={key}>
          {item.isHeader ? (
            <li className="menu-title">
              <span data-key="t-menu">{props.t(item.label)}</span>
            </li>
          ) : item.subItems ? (
            <li className="nav-item">
              <Link
                onClick={item.click}
                className="nav-link menu-link"
                to={item.link || "/#"}
                data-bs-toggle="collapse"
              >
                <i className={item.icon}></i>
                <span data-key="t-apps">{props.t(item.label)}</span>
                {item.badgeName && (
                  <span className={"badge badge-pill bg-" + item.badgeColor} data-key="t-new">
                    {item.badgeName}
                  </span>
                )}
              </Link>
              <Collapse className="menu-dropdown" isOpen={item.stateVariables} id="sidebarApps">
                <ul className="nav nav-sm flex-column">
                  {item.subItems.map((subItem: any, subKey: number) => (
                    <React.Fragment key={subKey}>
                      {!subItem.isChildItem ? (
                        <li className="nav-item">
                          <Link to={subItem.link || "/#"} className="nav-link">
                            {props.t(subItem.label)}
                            {subItem.badgeName && (
                              <span className={"badge badge-pill bg-" + subItem.badgeColor} data-key="t-new">
                                {subItem.badgeName}
                              </span>
                            )}
                          </Link>
                        </li>
                      ) : (
                        <li className="nav-item">
                          <Link
                            onClick={subItem.click}
                            className="nav-link"
                            to="/#"
                            data-bs-toggle="collapse"
                          >
                            {props.t(subItem.label)}
                            {subItem.badgeName && (
                              <span className={"badge badge-pill bg-" + subItem.badgeColor} data-key="t-new">
                                {subItem.badgeName}
                              </span>
                            )}
                          </Link>
                          <Collapse className="menu-dropdown" isOpen={subItem.stateVariables} id="sidebarEcommerce">
                            <ul className="nav nav-sm flex-column">
                              {subItem.childItems &&
                                subItem.childItems.map((childItem: any, childKey: number) => (
                                  <li className="nav-item" key={childKey}>
                                    <Link to={childItem.link || "/#"} className="nav-link" data-key="t-basic-action">
                                      {props.t(childItem.label)}
                                    </Link>
                                  </li>
                                ))}
                            </ul>
                          </Collapse>
                        </li>
                      )}
                    </React.Fragment>
                  ))}
                </ul>
              </Collapse>
            </li>
          ) : (
            <li className="nav-item">
              <Link className="nav-link menu-link" to={item.link || "/#"}>
                <i className={item.icon}></i>
                <span>{props.t(item.label)}</span>
                {item.badgeName && (
                  <span className={"badge badge-pill bg-" + item.badgeColor} data-key="t-new">
                    {item.badgeName}
                  </span>
                )}
              </Link>
            </li>
          )}
        </React.Fragment>
      ))}
    </>
  );
};

VerticalLayout.propTypes = {
  location: PropTypes.object,
  t: PropTypes.any,
};

export default withRouter(withTranslation()(VerticalLayout));
